<script>
import { onMounted, ref } from '@vue/composition-api'
import adminApi from '@/api/admin';
import router from '@/router';
import { supportedLocales } from '@/@core/utils/i18n_utils';

export default {
    props: ["screen"],
    setup(props) {
        const settings = ref({
            orientation: 1, // Landscape
            layout: 1, // Full
            show_footer: false,
            dark_mode: false,
            item_duration: 30,
            language: null
        });
        const isLoading = ref(false);
        const formRef = ref(null);

        // Constants from backend
        const orientationChoices = [
            { value: 1, text: 'Landscape (Horizontal)' },
            { value: 2, text: 'Portrait (Vertical)' }
        ];

        const layoutChoices = [
            { value: 1, text: 'Full Width' },
            { value: 2, text: 'Split Layout (70% / 30%)' }
        ];

        const fetchSettings = async () => {
            isLoading.value = true;
            const response = await adminApi.getInfoTvScreenSettings(props.screen);
            if (response.ok) {
                settings.value.orientation = response.data.orientation || 1;
                settings.value.layout = response.data.layout || 1;
                settings.value.show_footer = response.data.show_footer || false;
                settings.value.dark_mode = response.data.dark_mode || false;
                settings.value.item_duration = response.data.item_duration || 30;
                settings.value.language = response.data.language || null;
            }
            isLoading.value = false;
        }

        const submit = async () => {
            const valid = await formRef.value.validate();
            if (!valid) {
                return;
            }

            isLoading.value = true;
            const res = await adminApi.updateInfoTvScreenSettings(props.screen, {
                orientation: settings.value.orientation,
                layout: settings.value.layout,
                show_footer: settings.value.show_footer,
                dark_mode: settings.value.dark_mode,
                item_duration: settings.value.item_duration,
                language: settings.value.language
            });

            if (res.ok) {

            } else {
                alert(res.error || 'Virhe tallennuksessa');
            }
            isLoading.value = false;
        }

        onMounted(() => {
            fetchSettings();
        })

        return {
            settings,
            isLoading,
            submit,
            formRef,
            orientationChoices,
            layoutChoices,
            supportedLocales,
        }
    }
}

</script>
<template>
    <v-form ref="formRef" v-on:submit.prevent="submit">
        <v-row>
            <v-col cols="12">
                <div class="pb-3">
                    <strong>{{ $t('Näytön asetukset') }}</strong>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <!-- Left Side: Form Controls -->
            <v-col cols="12" lg="7">
                <!-- Orientation -->
                <v-row style="display: none;">
                    <v-col cols="12">
                        <v-select v-model="settings.orientation" :items="orientationChoices" item-text="text"
                            item-value="value" :label="$t('Näytön suunta')" outlined :disabled="isLoading"
                            hide-details="auto" class="mb-4" />
                    </v-col>
                </v-row>

                <!-- Layout -->
                <v-row>
                    <v-col cols="12">
                        <v-select v-model="settings.layout" :items="layoutChoices" item-text="text" item-value="value"
                            :label="$t('Ulkoasu')" outlined :disabled="isLoading" hide-details="auto" class="mb-4" />
                    </v-col>
                </v-row>

                <!-- Footer Toggle -->
                <v-row>
                    <v-col cols="12">
                        <v-switch v-model="settings.show_footer" :label="$t('Näytä alatunniste')" color="primary"
                            :disabled="isLoading" hide-details="auto" class="mb-2" />
                    </v-col>
                </v-row>

                <!-- Dark Mode -->
                <v-row>
                    <v-col cols="12">
                        <v-switch v-model="settings.dark_mode" :label="$t('Tumma tila')" color="primary"
                            :disabled="isLoading" hide-details="auto" class="mb-2" />
                    </v-col>
                </v-row>

                <!-- Language -->
                <v-row>
                    <v-col cols="12">
                        <v-select v-model="settings.language" :items="supportedLocales" item-text="title"
                            item-value="locale" :label="$t('Kieli')" outlined :disabled="isLoading" hide-details="auto"
                            class="mb-4" clearable />
                    </v-col>
                </v-row>

                <!-- Item Duration -->
                <v-row>
                    <v-col cols="12">
                        <v-text-field v-model.number="settings.item_duration"
                            :label="$t('Sisällön näyttöaika (sekuntia)')" outlined type="number" :min="10" :max="300"
                            :disabled="isLoading" :rules="[
                                v => !!v || 'Kenttä on pakollinen',
                                v => (v >= 10 && v <= 300) || 'Arvon tulee olla 10-300 sekuntia'
                            ]" hide-details="auto" class="mb-4" />
                        <div class="text-caption grey--text">
                            {{ $t('Aika jonka jokainen sisältö näytetään näytöllä (10-300 sekuntia)') }}
                        </div>
                    </v-col>
                </v-row>

                <!-- Submit Button -->
                <v-row>
                    <v-col>
                        <v-btn type="submit" color="primary" class="mr-3" :loading="isLoading" :disabled="isLoading">
                            {{ $t('Tallenna') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-col>

            <!-- Right Side: Layout Preview -->
            <v-col cols="12" lg="5">
                <v-card outlined>

                    <v-card-text>
                        <div class="pa-2">
                            <strong>{{ $t('Esikatselu') }}</strong>
                        </div>
                        <div :class="['layout-preview', settings.orientation === 2 ? 'portrait' : 'landscape']">
                            <!-- Header -->
                            <div class="preview-header">Header</div>

                            <!-- Main Content Area -->
                            <div class="preview-content">
                                <div :class="['preview-main', settings.layout === 2 ? 'split' : 'full']">
                                    Main{{ settings.layout === 2 ? ' (70%)' : '' }}
                                </div>
                                <div v-if="settings.layout === 2" class="preview-secondary">
                                    Secondary (30%)
                                </div>
                            </div>

                            <!-- Footer (conditional) -->
                            <div v-if="settings.show_footer" class="preview-footer">Footer</div>
                        </div>
                    </v-card-text>
                </v-card>

            </v-col>
        </v-row>
    </v-form>
</template>

<style scoped>
.layout-preview-container {
    background: #f5f5f5;
    padding: 16px;
    border-radius: 8px;
}

.layout-preview {
    border: 2px solid #999;
    background: white;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Landscape orientation */
.layout-preview.landscape {
    width: 400px;
    height: 225px;
    /* 16:9 ratio */
}

/* Portrait orientation */
.layout-preview.portrait {
    width: 225px;
    height: 400px;
    /* 9:16 ratio */
}

.preview-header {
    background: #1976d2;
    color: white;
    padding: 12px;
    text-align: center;
    font-size: 12px;
    font-weight: bold;
    border-bottom: 2px solid #999;
}

.preview-footer {
    background: #616161;
    color: white;
    padding: 8px;
    text-align: center;
    font-size: 11px;
    border-top: 2px solid #999;
}

.preview-content {
    flex: 1;
    display: flex;
    min-height: 0;
}

/* Landscape layouts */
.landscape .preview-content {
    flex-direction: row;
}

/* Portrait layouts */
.portrait .preview-content {
    flex-direction: column;
}

.preview-main {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e3f2fd;
    border: 1px dashed #1976d2;
    font-size: 11px;
    font-weight: 500;
    color: #1565c0;
}

.preview-main.full {
    flex: 1;
}

.preview-main.split {
    flex: 0 0 70%;
}

.preview-secondary {
    flex: 0 0 30%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff3e0;
    border: 1px dashed #f57c00;
    font-size: 10px;
    font-weight: 500;
    color: #e65100;
}

/* Border between split areas */
.landscape .preview-main.split {
    border-right: 2px solid #999;
}

.portrait .preview-main.split {
    border-bottom: 2px solid #999;
}
</style>
